import React from 'react';
import { AlertCircle, CheckCircle2, Loader2, X } from 'lucide-react';
import {
  ReputationGenerationRunStatus,
  type ReputationGeneratedTemplate,
} from '@reach/shared-types/automation-template';
import { forgetActiveGeneration, useActiveGeneration } from './activeGenerationStorage';
import {
  DISMISS_LABEL,
  GENERATION_BANNER_FAILED_FALLBACK,
  GENERATION_RUNNING_MESSAGE,
  GENERATION_STATUS_UNAVAILABLE_MESSAGE,
} from './reputation-defaults.constants';
import { useReputationGenerationStatus } from './useReputationDefaults';
import styles from './reputation-defaults-dialog.module.css';

const countGeneratedMessages = (templates: ReputationGeneratedTemplate[]): number =>
  templates.reduce(
    (generatedSoFar, template) =>
      generatedSoFar + template.messages.filter(message => message.status === 'generated').length,
    0,
  );

const countMessages = (templates: ReputationGeneratedTemplate[]): number =>
  templates.reduce((totalSoFar, template) => totalSoFar + template.messages.length, 0);

/**
 * Tells a partner how a generation run ended even if they closed the dialog. A one-shot
 * generation that fails must not fail unseen.
 */
export const ReputationGenerationBanner: React.FC = () => {
  const active = useActiveGeneration();
  const { data: run, gaveUp } = useReputationGenerationStatus(active?.jobId, {
    startedAt: active?.startedAt,
  });

  if (!active) {
    return null;
  }

  const dismissButton = (
    <button
      type="button"
      className={styles.bannerDismiss}
      onClick={forgetActiveGeneration}
      aria-label={DISMISS_LABEL}>
      <X size={14} />
    </button>
  );

  if (gaveUp) {
    return (
      <aside className={styles.bannerFailed}>
        <AlertCircle size={16} />
        {GENERATION_STATUS_UNAVAILABLE_MESSAGE}
        {dismissButton}
      </aside>
    );
  }

  const stillRunning = !run || run.status === ReputationGenerationRunStatus.PROCESSING;
  if (stillRunning) {
    return (
      <aside className={styles.bannerRunning}>
        <Loader2 size={16} />
        {GENERATION_RUNNING_MESSAGE}
        {dismissButton}
      </aside>
    );
  }

  const generatedCount = countGeneratedMessages(run.templates);
  const totalCount = countMessages(run.templates);
  const keptCopyCount = totalCount - generatedCount;

  const runFailedOutright = run.status === ReputationGenerationRunStatus.FAILED;
  const someMessagesKeptTheirCopy = keptCopyCount > 0;
  const needsAttention = runFailedOutright || someMessagesKeptTheirCopy;
  const keptCopyNote = someMessagesKeptTheirCopy ? ` ${keptCopyCount} kept their default wording.` : '';
  const summary = runFailedOutright
    ? (run.error ?? GENERATION_BANNER_FAILED_FALLBACK)
    : `Wrote ${generatedCount} of ${totalCount} messages.${keptCopyNote}`;

  return (
    <aside className={needsAttention ? styles.bannerFailed : styles.bannerDone}>
      {needsAttention ? <AlertCircle size={16} /> : <CheckCircle2 size={16} />}
      {summary}
      {dismissButton}
    </aside>
  );
};
